import { X } from "lucide-react";
import { type JSX, type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "@/lib/use-focus-trap";
import { cn } from "@/lib/utils";

// The open dialogs, oldest first (`unify-image-lightbox`). Every dialog listens
// for `Escape` on `document`, so when one opens on top of another (the image
// lightbox over the feedback form) only the last one — the topmost — may react;
// the one beneath keeps its state until the top one is gone. An entry is added
// when its dialog opens and removed when it closes or unmounts, whichever
// comes first.
const openDialogs: string[] = [];

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  ariaLabel: string;
}

export function Dialog({
  open,
  onClose,
  children,
  className,
  ariaLabel,
}: DialogProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const id = useId();

  useFocusTrap(ref, open);

  useEffect(() => {
    if (!open) return;
    openDialogs.push(id);
    return () => {
      const at = openDialogs.lastIndexOf(id);
      if (at !== -1) openDialogs.splice(at, 1);
    };
  }, [open, id]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      if (openDialogs[openDialogs.length - 1] === id) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, id]);

  if (!open) return <></>;

  return createPortal(
    <div
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative showModal/close calls
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Esc handler is registered above; the backdrop must stay non-focusable so the focus trap lands on the dialog content (see tests/components/use-focus-trap.test.tsx) */}
      <div aria-hidden="true" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        ref={ref}
        tabIndex={-1}
        className={cn(
          "relative z-10 max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-lg",
          className,
        )}
      >
        {children}
        <button
          type="button"
          aria-label="Cerrar"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
